<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
  <ul role="menu" @click="emits('selectRow', orderItem as DashboardItem)">
    <li
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-between gap-2"
      @click="emits('openDetails', orderItem as DashboardItem)"
      @keydown="triggerClickOnEnterOrSpace"
    >
      <span class="flex justify-start gap-2">
        <DocumentIcon class="text-ink size-5" /> Details
      </span>

      <span
        class="bg-[#ffae00] size-5 flex items-center justify-center leading-4 rounded-full"
        v-if="
          can('can-resolve-comment') &&
          currentTab !== 'urgent_action_items' &&
          (orderItem as DashboardItem).unresolved_comments_count > 0
        "
      >
        <p class="leading-none mb-[-1px]">
          {{ (orderItem as DashboardItem).unresolved_comments_count }}
        </p>
      </span>
    </li>
    <li
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      @click="
        emits(
          'changeFactory',
          (orderItem as DashboardItem).order.id,
          (orderItem as DashboardItem).id
        )
      "
      @keydown="triggerClickOnEnterOrSpace"
      v-if="can('change-factory') && (orderItem as DashboardItem).factory_id !== null"
    >
      <PencilSquareIcon class="text-ink size-5" /> Edit Factory
    </li>
    <li
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      @click="emits('copyOrder', (orderItem as DashboardItem).order.id)"
      @keydown="triggerClickOnEnterOrSpace"
      v-if="can('copy-order')"
    >
      <CopyIcon class="text-ink size-5" /> Copy Order
    </li>
    <li
      role="none"
      v-if="
        can('download-customer-pdf') &&
        (orderItem as DashboardItem).order?.additional_fields &&
        !(orderItem as DashboardItem).order?.additional_fields.hasOwnProperty('is_manual_order')
      "
    >
      <a
        role="menuitem"
        tabindex="0"
        @click="emits('viewPdf', (orderItem as DashboardItem).order.id)"
        @keydown="triggerClickOnEnterOrSpace"
        class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      >
        <DocumentTextIcon class="text-ink size-5" /> View Design Sheet
      </a>
    </li>
    <li
      role="menuitem"
      tabindex="0"
      @click="emits('openProvideQuote')"
      @keydown="triggerClickOnEnterOrSpace"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      v-if="
        (currentTab == 'quote_requested_items' && (orderItem as DashboardItem).status) ==
          'quote_requested' && (orderItem as DashboardItem).order.admin_salesrep_id == null
      "
    >
      <DocumentCurrencyDollarIcon class="text-ink size-5" /> Provide Quote
    </li>
    <template
      v-if="can('update-techpack') && !IsCustomTechpackUploaded(orderItem as DashboardItem)"
    >
      <!-- AB#21914 — the V2 editor first, the classic one kept while both exist. -->
      <li role="none">
        <a
          role="menuitem"
          :href="`/edit-techpack/${(orderItem as DashboardItem).order?.id}`"
          class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
          data-test="menu-edit-techpack-new"
        >
          <PencilSquareIcon class="text-ink size-5" />
          Edit Techpack <span class="text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 bg-[var(--accent-soft)] text-[var(--accent)]">new</span>
        </a>
      </li>
      <li role="none">
        <a
          :href="`${appURL}/edit-techpack/${(orderItem as DashboardItem).order?.id}?source=new_dashboard`"
          role="menuitem"
          class="px-2 py-2 text-ink-soft cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
          data-test="menu-edit-techpack-classic"
        >
          <PencilSquareIcon class="text-ink-soft size-5" />
          Edit Techpack (classic view)
        </a>
      </li>
    </template>

    <li
      v-if="can('update-custom-techpack')"
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      @click="emits('uploadCustomTechpack', orderItem as DashboardItem)"
      @keydown="triggerClickOnEnterOrSpace"
    >
      <DocumentTextIcon class="text-ink size-5" />
      <template v-if="!IsCustomTechpackUploaded(orderItem as DashboardItem)"
        >Upload Custom Techpack</template
      >
      <template v-else>View Custom Techpack</template>
    </li>
    <li
      v-if="
        currentTab == 'quote_requested_items' &&
        (orderItem as DashboardItem).status == 'quote_provided'
      "
      role="menuitem"
      tabindex="0"
      @click="emits('notifyCustomer', (orderItem as DashboardItem).order_id)"
      @keydown="triggerClickOnEnterOrSpace"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
    >
      <BellAlertIcon class="text-ink size-5" /> Notify Customer
    </li>
    <li
      v-if="can('delete-order')"
      role="menuitem"
      tabindex="0"
      @click="emits('deleteOrder', (orderItem as DashboardItem).order.id)"
      @keydown="triggerClickOnEnterOrSpace"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
    >
      <TrashIcon class="text-ink size-5" /> Delete order
    </li>
    <li
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      @click="emits('priceBreakdown', orderItem as DashboardItem)"
      @keydown="triggerClickOnEnterOrSpace"
      v-if="can('view-price-breakdown')"
    >
      <DocumentCurrencyDollarIcon class="size-5" />

      Order Price
      {{
        `${getOrderItemNetPriceCurrencyCode(orderItem as DashboardItem)} ` +
        `${getOrderItemPriceInfoDisplayAmount((orderItem as DashboardItem)?.price_info).toFixed(2)}`
      }}
    </li>
    <li
      class="text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2 px-2 py-2"
      v-if="can('update-order-urgency')"
    >
      <button class="flex items-center justify-between w-full gap-2">
        <ExclamationTriangleIcon class="size-5" />
        <Dropdown
          :customClass="'!w-full'"
          triggerStyle="flex w-full items-center justify-between  rounded-lg"
        >
          <template #trigger>
            Assign Urgency

            <ChevronRightIcon class="text-ink-soft size-4" />
          </template>

          <template #contents>
            <ul class="urgency-dropdown" role="menu">
              <li
                role="menuitem"
                tabindex="0"
                @click="emits('updateUrgency', 'vip', (orderItem as DashboardItem).order.id)"
                @keydown="triggerClickOnEnterOrSpace"
                class="flex items-center justify-start gap-2 px-2 py-2 text-center duration-300 border-b border-line cursor-pointer group hover:bg-green-100"
              >
                <p class="flex flex-row items-center justify-between gap-x-4">
                  <span
                    class="rounded-full bg-green-300 group-hover:bg-green-400 px-[10px] py-[10px]"
                  ></span>
                  <span>VIP</span>
                </p>
              </li>
              <li
                role="menuitem"
                tabindex="0"
                @click="emits('updateUrgency', 'urgent', (orderItem as DashboardItem).order.id)"
                @keydown="triggerClickOnEnterOrSpace"
                class="flex items-center justify-start gap-2 px-2 py-2 text-center duration-300 border-b border-line cursor-pointer group hover:bg-red-100"
              >
                <p class="flex flex-row items-center justify-between gap-x-4">
                  <span
                    class="rounded-full order_cancel group-hover:bg-red-300 px-[10px] py-[10px]"
                  ></span>
                  <span>Urgent</span>
                </p>
              </li>
              <li
                role="menuitem"
                tabindex="0"
                @click="
                  emits('updateUrgency', 'extra_attention', (orderItem as DashboardItem).order.id)
                "
                @keydown="triggerClickOnEnterOrSpace"
                class="flex items-center justify-start gap-2 px-2 py-2 text-center duration-300 border-b border-line cursor-pointer group hover:bg-blue-100"
              >
                <p class="flex flex-row items-center justify-between gap-x-4">
                  <span
                    class="rounded-full bg-blue-300 group-hover:bg-blue-400 px-[10px] py-[10px]"
                  ></span>
                  <span>Extra Attention</span>
                </p>
              </li>
              <li
                role="menuitem"
                tabindex="0"
                @click="emits('updateUrgency', null, (orderItem as DashboardItem).order.id)"
                @keydown="triggerClickOnEnterOrSpace"
                :class="[
                  (orderItem as DashboardItem).order.urgency,
                  {
                    'pointer-events-none opacity-50':
                      (orderItem as DashboardItem).order.urgency == null,
                  },
                ]"
                class="flex items-center justify-start gap-2 px-2 py-2 text-center duration-300 cursor-pointer group hover:bg-surface-2"
              >
                <p class="flex flex-row items-center justify-between gap-x-4">
                  <span
                    class="rounded-full bg-surface-3 group-hover:bg-surface-3 px-[10px] py-[10px]"
                  ></span>
                  <span>Remove Urgency</span>
                </p>
              </li>
            </ul>
          </template>
        </Dropdown>
      </button>
    </li>
    <!-- TechPack URL -->
    <li
      v-if="viewDefaultTechpacks"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
    >
      <template v-if="app_environment === 'Production'">
        <template v-if="(orderItem as DashboardItem).order?.id > 115">
          <button
            @click="emits('fetchPdf', (orderItem as DashboardItem).order, 0)"
            :class="{
              'pt-0 pb-0 pr-0': (orderItem as DashboardItem).techpack_data_count === 0,
            }"
          >
            <DocumentIcon class="size-5" />
            <div class="flex items-center justify-between w-full gap-2">
              Techpack pdf
              <Dropdown
                v-if="(orderItem as DashboardItem).techpack_data_count > 0"
                triggerStyle="rounded-lg"
              >
                <template #trigger>
                  <ChevronRightIcon class="text-ink-soft size-4" />
                </template>

                <template #contents>
                  <ul role="menu">
                    <li
                      role="menuitem"
                      tabindex="0"
                      :key="`order-item-version-${techpackDataVersion}`"
                      @click="
                        emits(
                          'fetchPdf',
                          (orderItem as DashboardItem).order,
                          techpackDataVersion + 1
                        )
                      "
                      @keydown="triggerClickOnEnterOrSpace"
                      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                      v-for="techpackDataVersion in orderItem.techpack_data_count"
                    >
                      Version {{ techpackDataVersion + 1 }}
                    </li>
                  </ul>
                </template>
              </Dropdown>
            </div>
          </button>
        </template>
      </template>
      <template v-else>
        <button
          class="flex items-center justify-between w-full gap-2"
          @click="emits('fetchPdf', (orderItem as DashboardItem).order, 0)"
          :class="{
            'pt-0 pb-0 pr-0': (orderItem as DashboardItem).techpack_data_count === 0,
          }"
        >
          <DocumentIcon class="size-5" />
          <div class="flex items-center justify-between w-full gap-2">
            Techpack pdf
            <Dropdown triggerStyle="" v-if="(orderItem as DashboardItem).techpack_data_count > 0">
              <template #trigger>
                <ChevronRightIcon class="text-ink-soft size-4" />
              </template>
              <template #contents>
                <ul role="menu">
                  <template v-if="(orderItem as DashboardItem).techpack_data_count > 0">
                    <li
                      role="menuitem"
                      tabindex="0"
                      :key="`order-item-version-${techpackDataVersion}`"
                      @click="
                        emits('fetchPdf', (orderItem as DashboardItem).order, techpackDataVersion)
                      "
                      @keydown="triggerClickOnEnterOrSpace"
                      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                      v-for="techpackDataVersion in orderItem.techpack_data_count"
                    >
                      Version {{ techpackDataVersion }}
                    </li>
                  </template>
                </ul>
              </template>
            </Dropdown>
          </div>
        </button>
      </template>
    </li>
    <!-- < MERCHANT URLS -->
    <li
      v-if="viewMerchatTechpacks"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
    >
      <template v-if="app_environment === 'Production'">
        <template v-if="(orderItem as DashboardItem).order?.id > 115">
          <button
            @click="
              onDownloadClick(
                merchant_techpack_urls[0],
                `${storage_url}/${merchant_techpack_urls[0]}`,
                `merchant_${(orderItem as DashboardItem).order.order_no}_techpack_v0.pdf`
              )
            "
            class="flex justify-between w-full"
            :class="{
              'pt-0 pb-0 pr-0': merchant_techpack_urls.length === 0,
            }"
          >
            <UserIcon class="size-5" />
            <div class="flex items-center justify-between w-full gap-2 pl-2">
              Techpack pdf
              <Dropdown v-if="merchant_techpack_urls.length > 1" triggerStyle="rounded-lg">
                <template #trigger>
                  <ChevronRightIcon class="text-ink-soft size-4" />
                </template>

                <template #contents>
                  <ul role="menu">
                    <template
                      v-if="merchant_techpack_urls.length > 1"
                      :key="`order-item-version-${techpackDataVersion}`"
                    >
                      <li
                        role="menuitem"
                        tabindex="0"
                        v-for="(techpackDataVersion, techpackIndex) in merchant_techpack_urls.slice(
                          1
                        )"
                        @click="
                          onDownloadClick(
                            techpackDataVersion,
                            `${storage_url}/${techpackDataVersion}`,
                            `merchant_${(orderItem as DashboardItem).order.order_no}_techpack_v${techpackIndex + 1}.pdf`
                          )
                        "
                        @keydown="triggerClickOnEnterOrSpace"
                        class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                      >
                        Version {{ techpackIndex + 1 }}
                      </li>
                    </template>
                  </ul>
                </template>
              </Dropdown>
            </div>
          </button>
        </template>
      </template>
      <template v-else>
        <button
          class="flex justify-between w-full"
          :class="{
            'pt-0 pb-0 pr-0': merchant_techpack_urls.length === 0,
          }"
          @click="
            onDownloadClick(
              merchant_techpack_urls[0],
              `${storage_url}/${merchant_techpack_urls[0]}`,
              `merchant_${(orderItem as DashboardItem).order.order_no}_techpack_v0.pdf`
            )
          "
        >
          <UserIcon class="size-5" />
          <div class="flex items-center justify-between w-full gap-2 pl-2">
            Techpack pdf
            <Dropdown v-if="merchant_techpack_urls.length > 1" triggerStyle="rounded-lg">
              <template #trigger>
                <ChevronRightIcon class="text-ink-soft size-4" />
              </template>

              <template #contents>
                <ul role="menu">
                  <template
                    v-if="merchant_techpack_urls.length > 1"
                    :key="`order-item-version-${techpackDataVersion}`"
                  >
                    <li
                      role="menuitem"
                      tabindex="0"
                      v-for="(techpackDataVersion, techpackIndex) in merchant_techpack_urls.slice(
                        1
                      )"
                      @click="
                        onDownloadClick(
                          techpackDataVersion,
                          `${storage_url}/${techpackDataVersion}`,
                          `merchant_${(orderItem as DashboardItem).order.order_no}_techpack_v${techpackIndex + 1}.pdf`
                        )
                      "
                      @keydown="triggerClickOnEnterOrSpace"
                      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                    >
                      Version {{ techpackIndex + 1 }}
                    </li>
                  </template>
                </ul>
              </template>
            </Dropdown>
          </div>
        </button>
      </template>
    </li>
    <!-- Factory URLS -->
    <li
      v-if="viewFactoryTechpacks"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
    >
      <template v-if="app_environment === 'Production'">
        <template v-if="(orderItem as DashboardItem).order?.id > 115">
          <!-- <li class="flex justify-between w-full"> -->
          <button
            class="flex justify-between w-full"
            :class="{
              'pt-0 pb-0 pr-0': factory_techpack_urls.length === 0,
            }"
            @click="
              onDownloadClick(
                factory_techpack_urls[0],
                `${storage_url}/${factory_techpack_urls[0]}`,
                `factory_${(orderItem as DashboardItem).order.order_no}_techpack_v0.pdf`
              )
            "
          >
            <BuildingOfficeIcon class="size-5" />
            <div class="flex items-center justify-between w-full gap-2">
              Techpack pdf
              <Dropdown v-if="factory_techpack_urls.length > 1" triggerStyle="rounded-lg">
                <template #trigger>
                  <ChevronRightIcon class="text-ink-soft size-4" />
                </template>

                <template #contents>
                  <ul role="menu">
                    <template
                      v-for="(techpackDataVersion, techpackIndex) in factory_techpack_urls.slice(1)"
                      :key="`order-item-version-${techpackIndex}`"
                    >
                      <li
                        role="menuitem"
                        tabindex="0"
                        @click="
                          onDownloadClick(
                            techpackDataVersion,
                            `${storage_url}/${techpackDataVersion}`,
                            `factory_${(orderItem as DashboardItem).order.order_no}_techpack_v${techpackIndex + 1}.pdf`
                          )
                        "
                        @keydown="triggerClickOnEnterOrSpace"
                        class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                      >
                        Version {{ techpackIndex + 1 }}
                      </li>
                    </template>
                  </ul>
                </template>
              </Dropdown>
            </div>
          </button>
          <!-- </li> -->
        </template>
      </template>
      <template v-else>
        <button
          class="flex items-center justify-between w-full gap-2"
          :class="{
            'pt-0 pb-0 pr-0': factory_techpack_urls.length === 0,
          }"
          @click="
            onDownloadClick(
              factory_techpack_urls[0],
              `${storage_url}/${factory_techpack_urls[0]}`,
              `factory_${(orderItem as DashboardItem).order.order_no}_techpack_v0.pdf`
            )
          "
        >
          <BuildingOfficeIcon class="size-5" />
          <div class="flex items-center justify-between w-full gap-2">
            Techpack pdf
            <Dropdown triggerStyle="" v-if="factory_techpack_urls.length > 1">
              <template #trigger>
                <ChevronRightIcon class="text-ink-soft size-4" />
              </template>
              <template #contents>
                <ul role="menu">
                  <template
                    v-if="factory_techpack_urls.length > 1"
                    :key="`order-item-version-${techpackDataVersion}`"
                  >
                    <li
                      role="menuitem"
                      tabindex="0"
                      v-for="(techpackDataVersion, techpackIndex) in factory_techpack_urls.slice(1)"
                      @click="
                        onDownloadClick(
                          techpackDataVersion,
                          `${storage_url}/${techpackDataVersion}`,
                          `factory_${(orderItem as DashboardItem).order.order_no}_techpack_v${techpackIndex + 1}.pdf`
                        )
                      "
                      @keydown="triggerClickOnEnterOrSpace"
                      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
                    >
                      Version {{ techpackIndex + 1 }}
                    </li>
                  </template>
                </ul>
              </template>
            </Dropdown>
          </div>
        </button>
      </template>
    </li>
    <li
      v-if="can('add-internal-order-comment')"
      role="menuitem"
      tabindex="0"
      class="px-2 py-2 text-ink cursor-pointer duration-300 hover:bg-surface-2 flex items-center justify-start gap-2"
      @click="emits('openInternalNote')"
      @keydown="triggerClickOnEnterOrSpace"
    >
      <ChatBubbleBottomCenterTextIcon class="text-ink size-5" /> Internal order comment
    </li>
  </ul>
</template>
<script setup lang="ts">
import { can, downloadFile, storage_url } from '@/composables';
import { showToast } from '@/composables/swal';
import { DashboardItem } from '@/models';
import {
  getOrderItemNetPriceCurrencyCode,
  getOrderItemPriceInfoDisplayAmount,
} from '@/utils/orderItemPriceDisplay';
import {
  BuildingOfficeIcon,
  DocumentCurrencyDollarIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/vue/24/outline';
import {
  BellAlertIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronRightIcon,
  DocumentIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/vue/24/solid';
import { CopyIcon } from 'lucide-vue-next';
import { computed, ComputedRef } from 'vue';
import Dropdown from '../Shared/Dropdown.vue';
import { triggerClickOnEnterOrSpace } from '@/utils/a11y';

const app_environment = import.meta.env.VITE_APP_NODE_ENV;
const appURL = import.meta.env.VITE_VUE2_APP_URL;

const props = defineProps<{
  orderItem: any;
  currentTab: string;
}>();

const IsCustomTechpackUploaded = (orderItem: DashboardItem) => {
  return !!orderItem.order.custom_techpack_pdf;
};

const emits = defineEmits([
  'selectRow',
  'openDetails',
  'changeFactory',
  'viewPdf',
  'openProvideQuote',
  'notifyCustomer',
  'deleteOrder',
  'priceBreakdown',
  'updateUrgency',
  'fetchPdf',
  'openInternalNote',
  'uploadCustomTechpack',
  'copyOrder',
]);

const factory_techpack_urls: ComputedRef<Array<any>> = computed(() => {
  if ((props.orderItem as DashboardItem).factory_techpack_pdf_urls) {
    // if ((props.orderItem as DashboardItem).factory_techpack_pdf_urls.some((url) => url !== null)) {
    return (props.orderItem as DashboardItem).factory_techpack_pdf_urls.filter(
      (url) => url !== null
    );
    // } else {
    //   return [];
    // }
  }
  return [];
});

const onDownloadClick = (base_path: string | null, url: string, title: string) => {
  if (base_path) {
    downloadFile(url, title);
  } else {
    showToast('The file isn\'t ready yet. Try again in a few minutes.', 'info');
  }
};
const merchant_techpack_urls: ComputedRef<Array<any>> = computed(() => {
  if ((props.orderItem as DashboardItem).order.merchant_techpack_pdf_urls) {
    return (props.orderItem as DashboardItem).order.merchant_techpack_pdf_urls.filter(
      (url) => url !== null
    );
  }
  return [];
});

const viewDefaultTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-merchant-pdf')) {
    return (
      merchant_techpack_urls.value.every((url) => url === null) ||
      merchant_techpack_urls.value.length === 0
    );
  } else if (can('view-factory-pdf')) {
    return (
      factory_techpack_urls.value.every((url) => url === null) ||
      factory_techpack_urls.value.length === 0
    );
  } else {
    return false;
  }
});

const viewMerchatTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-merchant-pdf')) {
    return (
      merchant_techpack_urls.value.length > 0 &&
      merchant_techpack_urls.value.some((url) => url !== null)
    );
  }
});

const viewFactoryTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-factory-pdf')) {
    return (
      factory_techpack_urls.value.length > 0 &&
      factory_techpack_urls.value.some((url) => url !== null)
    );
  }
});
</script>
